/**
 * Listing image master-format helpers — pure sizing / duplicate / query guards.
 * Used by client compress + upload paths so masters stay WebP/JPEG and ≤ max edge.
 */

/**
 * Scale width/height so the longer edge is ≤ maxWidthOrHeight.
 * Returns original dimensions when already within the limit (or invalid inputs).
 */
export function scaleImageDimensions(
  width: number,
  height: number,
  maxWidthOrHeight: number,
): { width: number; height: number } {
  if (
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    !Number.isFinite(maxWidthOrHeight) ||
    width <= 0 ||
    height <= 0 ||
    maxWidthOrHeight <= 0
  ) {
    return { width: Math.max(0, width), height: Math.max(0, height) };
  }

  const longest = Math.max(width, height);
  if (longest <= maxWidthOrHeight) {
    return { width, height };
  }

  const scale = maxWidthOrHeight / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * True when candidate content hash already appears in the draft/selection set.
 */
export function listingDraftHasMatchingContentHash(
  existingHashes: readonly (string | null | undefined)[],
  candidateHash: string,
): boolean {
  const normalized = candidateHash.trim().toLowerCase();
  if (!normalized) return false;
  return existingHashes.some(
    (hash) => (hash?.trim().toLowerCase() ?? "") === normalized,
  );
}

/**
 * Collect content hashes already present on draft/selected images.
 * Prefers `image.hash`, then metadata.hash (upload draft path).
 */
export function collectListingDraftContentHashes(
  images: ReadonlyArray<{
    hash?: string | null;
    metadata?: { hash?: string | null } | null;
  }>,
): string[] {
  const hashes: string[] = [];
  for (const image of images) {
    const fromRow = image.hash?.trim();
    const fromMeta =
      image.metadata && typeof image.metadata === "object"
        ? image.metadata.hash?.trim()
        : undefined;
    const value = fromRow || fromMeta;
    if (value) hashes.push(value);
  }
  return hashes;
}

/**
 * PostgREST `.or()` filter for a targeted image_hash / image_signature lookup.
 * Hex-only sanitization prevents filter injection.
 */
export function buildItemImageContentHashOrFilter(
  hash: string,
  signature: string,
): string | null {
  const safeHash = hash.replace(/[^a-fA-F0-9]/g, "");
  const safeSignature = signature.replace(/[^a-fA-F0-9]/g, "");
  if (!safeHash && !safeSignature) return null;

  const parts: string[] = [];
  if (safeHash) parts.push(`image_hash.eq.${safeHash}`);
  if (safeSignature) parts.push(`image_signature.eq.${safeSignature}`);
  return parts.join(",");
}
